import { useEffect, useState } from 'react';

export default function HomePage() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-white/95 backdrop-blur-md shadow-sm' : 'bg-transparent'
      }`}>
        <div className="max-w-7xl mx-auto px-8 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img 
              src="https://public.readdy.ai/ai/img_res/c881ca31-adc5-45a8-a010-aa4d5e8bc4e6.png" 
              alt="NIMECU LTDA Logo" 
              className="h-10 w-10 object-contain"
            />
            <span className={`text-xl font-bold transition-colors ${
              scrolled ? 'text-gray-900' : 'text-white'
            }`}>NIMECU LTDA</span>
          </div>
          <div className="flex items-center gap-12">
            <a href="#about" className={`text-sm font-medium transition-colors cursor-pointer ${
              scrolled ? 'text-gray-700 hover:text-gray-900' : 'text-white/90 hover:text-white'
            }`}>About</a>
            <a href="#products" className={`text-sm font-medium transition-colors cursor-pointer ${
              scrolled ? 'text-gray-700 hover:text-gray-900' : 'text-white/90 hover:text-white'
            }`}>Products</a>
            <a href="#features" className={`text-sm font-medium transition-colors cursor-pointer ${
              scrolled ? 'text-gray-700 hover:text-gray-900' : 'text-white/90 hover:text-white'
            }`}>Features</a>
            <a href="#contact" className={`text-sm font-medium transition-colors cursor-pointer ${
              scrolled ? 'text-gray-700 hover:text-gray-900' : 'text-white/90 hover:text-white'
            }`}>Contact</a>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://public.readdy.ai/ai/img_res/3b91bf5e5986828dd7aed3d72eba1956.jpg" 
            alt="Hero Background" 
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-8 w-full">
          <div className="max-w-4xl">
            <h1 className="text-8xl font-bold text-white leading-tight mb-8" style={{ letterSpacing: '-2px' }}>
              Modern iOS<br />
              Applications<br />
              Redefined
            </h1>
            <div className="flex flex-col gap-6 max-w-2xl">
              <div className="bg-white/95 backdrop-blur-sm rounded-3xl p-8 shadow-2xl">
                <p className="text-gray-800 text-base leading-relaxed">
                  Expert team developing cutting-edge VPN and Cleaner solutions for the iOS platform, enhancing user experience and improving mobile device security.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-32 bg-white">
        <div className="max-w-7xl mx-auto px-8">
          <div className="grid grid-cols-2 gap-16 items-center">
            <div className="relative">
              <img 
                src="https://readdy.ai/api/search-image?query=Professional%20software%20development%20team%20collaborating%20in%20modern%20office%20environment%20with%20clean%20minimalist%20design%2C%20iOS%20developers%20working%20together%20on%20innovative%20mobile%20applications%2C%20bright%20workspace%20with%20natural%20lighting%20and%20contemporary%20aesthetic%2C%20technology%20innovation%20atmosphere&width=800&height=900&seq=about-team-001&orientation=portrait" 
                alt="NIMECU LTDA Team" 
                className="w-full h-[600px] object-cover rounded-[32px] shadow-xl"
              />
              <div className="absolute top-8 left-8 flex gap-3">
                <div className="px-6 py-3 bg-white/20 backdrop-blur-md rounded-full text-white text-sm font-medium">
                  Innovation
                </div>
                <div className="px-6 py-3 bg-white/20 backdrop-blur-md rounded-full text-white text-sm font-medium">
                  Quality
                </div>
                <div className="px-6 py-3 bg-white/20 backdrop-blur-md rounded-full text-white text-sm font-medium">
                  Trust
                </div>
              </div>
            </div>
            
            <div className="space-y-8">
              <div>
                <h2 className="text-5xl font-bold text-gray-900 mb-6">NIMECU LTDA</h2>
                <div className="flex flex-wrap gap-3 mb-8">
                  <span className="px-4 py-2 border border-gray-300 rounded-full text-sm text-gray-700">Est. 2024</span>
                  <span className="px-4 py-2 text-sm text-gray-600">iOS Development</span>
                  <span className="px-4 py-2 text-sm text-gray-600">Security</span>
                  <span className="px-4 py-2 text-sm text-gray-600">Performance</span>
                </div>
              </div>
              
              <div className="bg-slate-900 rounded-3xl p-10 relative overflow-hidden">
                <div className="absolute top-6 left-10">
                  <span className="px-3 py-1 bg-lime-400 text-slate-900 text-xs font-bold rounded-md">ABOUT US</span>
                </div>
                <div className="mt-12 relative z-10">
                  <h3 className="text-3xl font-bold text-white mb-6">Expert Team</h3>
                  <p className="text-white/90 text-base leading-relaxed">
                    NIMECU LTDA is a team of experts dedicated to developing modern applications for the iOS platform. Our products, including VPN and Cleaner, are meticulously designed to enhance user experience and improve the security of mobile devices. We combine cutting-edge technology with user-centric design to deliver exceptional solutions.
                  </p>
                </div>
                <div className="absolute right-0 bottom-0 w-64 h-64 opacity-10">
                  <img 
                    src="https://readdy.ai/api/search-image?query=Abstract%20geometric%20pattern%20with%20interconnected%20nodes%20and%20lines%20representing%20technology%20network%2C%20minimalist%20design%20in%20white%20and%20blue%20tones%2C%20digital%20innovation%20concept%20with%20clean%20modern%20aesthetic&width=400&height=400&seq=about-pattern-001&orientation=squarish" 
                    alt="Pattern" 
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section id="products" className="py-32 bg-gray-50">
        <div className="max-w-7xl mx-auto px-8">
          <div className="text-center mb-20">
            <div className="inline-block px-4 py-2 bg-blue-500/10 rounded-full mb-4">
              <span className="text-xs font-bold text-blue-600 uppercase tracking-wider">OUR PRODUCTS</span>
            </div>
            <h2 className="text-7xl font-bold mb-6">
              <span className="text-gray-900">VPN</span> <span className="text-blue-500">&amp; Cleaner</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Enhanced security and performance for your iOS devices
            </p>
            <div className="absolute top-8 right-8">
              <span className="px-6 py-2 bg-gradient-to-r from-blue-500 to-purple-500 text-white text-sm font-medium rounded-full">
                iOS Exclusive
              </span>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-8">
            <div className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-xl transition-shadow">
              <div className="mb-6">
                <img 
                  src="https://readdy.ai/api/search-image?query=Modern%20VPN%20security%20shield%20icon%20with%20geometric%20patterns%20in%20blue%20and%20purple%20gradients%2C%20minimalist%20iOS%20app%20interface%20design%20showing%20encrypted%20connection%20and%20privacy%20protection%2C%20clean%20professional%20technology%20illustration%20with%20simple%20background&width=600&height=400&seq=vpn-product-001&orientation=landscape" 
                  alt="VPN Product" 
                  className="w-full h-80 object-cover rounded-2xl"
                />
              </div>
              <div className="mb-4">
                <span className="px-4 py-2 bg-blue-100 text-blue-700 text-xs font-bold rounded-full">VPN</span>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">Secure VPN Application</h3>
              <p className="text-gray-600 text-sm leading-relaxed mb-4">
                Military-grade encryption protecting your online privacy and security. Fast, reliable connections with servers worldwide. Browse safely and anonymously on your iOS device.
              </p>
              <div className="text-right">
                <span className="text-3xl font-bold text-gray-900">v2.5</span>
              </div>
            </div>

            <div className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-xl transition-shadow">
              <div className="mb-6">
                <img 
                  src="https://readdy.ai/api/search-image?query=iOS%20cleaner%20app%20interface%20with%20minimalist%20design%20showing%20storage%20optimization%20and%20performance%20boost%2C%20green%20and%20teal%20color%20scheme%20with%20clean%20icons%2C%20modern%20mobile%20application%20dashboard%20with%20simple%20background%20highlighting%20efficiency%20and%20speed&width=600&height=400&seq=cleaner-product-001&orientation=landscape" 
                  alt="Cleaner Product" 
                  className="w-full h-80 object-cover rounded-2xl"
                />
              </div>
              <div className="mb-4">
                <span className="px-4 py-2 bg-green-100 text-green-700 text-xs font-bold rounded-full">CLEANER</span>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">Intelligent Cleaner Tool</h3>
              <p className="text-gray-600 text-sm leading-relaxed mb-4">
                Optimize your device performance with intelligent cleaning algorithms. Remove junk files, manage storage efficiently, and keep your iOS device running smoothly and fast.
              </p>
              <div className="text-right">
                <span className="text-3xl font-bold text-gray-900">v3.1</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-32 bg-gray-100">
        <div className="max-w-7xl mx-auto px-8">
          <div className="flex justify-between items-start mb-20">
            <div>
              <h2 className="text-6xl font-bold text-gray-400 mb-2">Technical</h2>
              <h2 className="text-7xl font-bold text-gray-900 italic font-serif">Excellence</h2>
            </div>
            <div className="max-w-xl">
              <p className="text-lg text-gray-700 leading-relaxed">
                Our development approach combines cutting-edge technology with meticulous attention to detail, ensuring every application we create meets the highest standards of quality and performance.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-8">
            <div className="bg-white rounded-3xl overflow-hidden shadow-lg">
              <div className="p-8">
                <img 
                  src="https://readdy.ai/api/search-image?query=iPhone%20mockup%20displaying%20VPN%20security%20interface%20with%20encryption%20indicators%20and%20connection%20status%2C%20modern%20iOS%20app%20design%20with%20blue%20and%20purple%20color%20scheme%2C%20clean%20professional%20mobile%20screen%20showing%20privacy%20protection%20features%20on%20simple%20background&width=500&height=600&seq=feature-security-001&orientation=portrait" 
                  alt="Security Feature" 
                  className="w-full h-64 object-cover rounded-2xl mb-6"
                />
                <div className="mb-4">
                  <span className="px-4 py-2 bg-lime-400 text-gray-900 text-xs font-bold rounded-full">SECURITY</span>
                </div>
                <h3 className="text-3xl font-bold text-gray-900 leading-tight">Military-Grade Encryption</h3>
              </div>
            </div>

            <div className="bg-slate-900 rounded-3xl overflow-hidden shadow-lg flex flex-col items-center justify-center p-8">
              <div className="w-32 h-32 flex items-center justify-center mb-8">
                <i className="ri-speed-up-line text-8xl text-lime-400"></i>
              </div>
              <div className="mb-4">
                <span className="px-4 py-2 bg-lime-400 text-gray-900 text-xs font-bold rounded-full">PERFORMANCE</span>
              </div>
              <h3 className="text-3xl font-bold text-white text-center leading-tight">Intelligent Optimization</h3>
            </div>

            <div className="bg-white rounded-3xl overflow-hidden shadow-lg">
              <div className="p-8">
                <img 
                  src="https://readdy.ai/api/search-image?query=iOS%20cleaner%20app%20analytics%20dashboard%20showing%20storage%20usage%20graphs%20and%20performance%20metrics%2C%20modern%20mobile%20interface%20with%20clean%20design%20and%20data%20visualization%2C%20teal%20and%20green%20color%20scheme%20with%20simple%20background%20highlighting%20efficiency%20statistics&width=500&height=600&seq=feature-analytics-001&orientation=portrait" 
                  alt="Analytics Feature" 
                  className="w-full h-64 object-cover rounded-2xl mb-6"
                />
                <div className="mb-4">
                  <span className="px-4 py-2 bg-blue-100 text-blue-700 text-xs font-bold rounded-full">ANALYTICS</span>
                </div>
                <h3 className="text-3xl font-bold text-gray-900 leading-tight">Real-Time Monitoring</h3>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-32 bg-white">
        <div className="max-w-7xl mx-auto px-8">
          <div className="relative">
            <div className="w-full h-[600px] rounded-3xl overflow-hidden shadow-2xl">
              <img 
                src="https://readdy.ai/api/search-image?query=Close-up%20artistic%20shot%20of%20iPhone%20with%20modern%20iOS%20interface%20displaying%20security%20and%20performance%20features%2C%20abstract%20technology%20visualization%20with%20blue%20and%20purple%20lighting%2C%20professional%20product%20photography%20with%20artistic%20grain%20and%20depth%20of%20field%20on%20simple%20gradient%20background&width=1400&height=700&seq=cta-visual-001&orientation=landscape" 
                alt="iOS Experience" 
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="text-center mt-16">
              <h2 className="text-5xl leading-tight mb-12">
                <span className="text-gray-400 font-light">Experience the </span>
                <span className="text-gray-900 font-bold italic">future</span>
                <span className="text-gray-900 font-bold"> of iOS</span><br />
                <span className="text-gray-400 font-light">security today</span>
              </h2>
              <div className="inline-flex items-center justify-center px-12 py-4 bg-gray-900 text-white text-base font-medium rounded-full">
                Explore Products
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <footer id="contact" className="bg-emerald-950 text-white py-24">
        <div className="max-w-7xl mx-auto px-8">
          <div className="grid grid-cols-4 gap-12 mb-20">
            <div>
              <div className="flex items-center gap-3 mb-6">
                <img 
                  src="https://public.readdy.ai/ai/img_res/c881ca31-adc5-45a8-a010-aa4d5e8bc4e6.png" 
                  alt="NIMECU LTDA Logo" 
                  className="h-8 w-8 object-contain"
                />
                <span className="text-xl font-bold">NIMECU LTDA</span>
              </div>
              <div className="space-y-2 text-sm text-gray-400">
                <p>iOS Development Specialists</p>
                <p>VPN &amp; Cleaner Solutions</p>
                <p>Trusted Security Partner</p>
              </div>
            </div>

            <div>
              <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-6">CONTACT</h3>
              <div className="space-y-4">
                <div className="flex items-center gap-2 text-sm">
                  <i className="ri-mail-line w-5 h-5 flex items-center justify-center"></i>
                  <span>Email</span>
                </div>
                <p className="text-white">info@nimecultda.com</p>
                <div className="flex items-center gap-2 text-sm mt-6">
                  <i className="ri-phone-line w-5 h-5 flex items-center justify-center"></i>
                  <span>Phone</span>
                </div>
                <p className="text-white">+1 5418542914</p>
              </div>
            </div>

            <div>
              <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-6">PRODUCTS</h3>
              <div className="space-y-5 text-base">
                <p className="text-white cursor-pointer hover:text-lime-400 transition-colors">VPN Application</p>
                <p className="text-white cursor-pointer hover:text-lime-400 transition-colors">Cleaner Tool</p>
                <p className="text-white cursor-pointer hover:text-lime-400 transition-colors">Security Suite</p>
              </div>
            </div>

            <div>
              <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-6">COMPANY</h3>
              <div className="space-y-5 text-base">
                <p className="text-white cursor-pointer hover:text-lime-400 transition-colors">About Us</p>
                <p className="text-white cursor-pointer hover:text-lime-400 transition-colors">Technology</p>
                <p className="text-white cursor-pointer hover:text-lime-400 transition-colors">Careers</p>
              </div>
            </div>
          </div>

          <div className="border-t border-white/10 pt-16">
            <div className="mb-8">
              <h2 className="text-8xl font-bold text-amber-100 mb-4" style={{ letterSpacing: '-3px' }}>
                CARRERA 9 31 41 • QUIBDO
              </h2>
              <h2 className="text-8xl font-bold text-amber-100" style={{ letterSpacing: '-3px' }}>
                270001 • COLOMBIA
              </h2>
            </div>
            
            <div className="flex items-center justify-between text-sm text-gray-400 pt-8 border-t border-white/10">
              <p>© 2024 NIMECU LTDA. All rights reserved.</p>
              <a 
                href="https://readdy.ai/?ref=logo" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="hover:text-white transition-colors cursor-pointer"
              >
                Powered by Readdy
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
